/**
 * A casca do aplicativo: decide quem está entrando e qual tela mostrar.
 *
 * A DECISÃO QUE VALE A PENA ENTENDER AQUI é como o app se comporta sem sinal.
 * Há duas maneiras de um pedido ao servidor não dar certo, e tratá-las igual
 * seria um desastre no campo:
 *
 *   O servidor respondeu "não"  → o crachá foi revogado ou a pessoa desligada.
 *                                 Aí sim volta para a tela do código.
 *   Não houve resposta          → o celular está sem sinal na beira do tanque.
 *                                 Aí o app CONTINUA aberto, com o que já sabe.
 *
 * Confundir as duas jogaria o tratador para fora do app toda vez que ele
 * entrasse numa área sem cobertura — que é justamente onde ele trabalha.
 */

import { useCallback, useEffect, useState } from 'react'
import { assinar, quantosPendentes, sincronizar } from './acesso/fila'
import { Acessos } from './telas/Acessos'
import { Fila, NOME } from './telas/Fila'
import { Biometria } from './telas/Biometria'
import { Despesca } from './telas/Despesca'
import { Romaneios } from './telas/Romaneios'
import { Movimentacao } from './telas/Movimentacao'
import { Recepcao } from './telas/Recepcao'
import { Aplicacao } from './telas/Aplicacao'
import { Perdas } from './telas/Perdas'
import { Ciclos } from './telas/Ciclos'
import { Entrada } from './telas/Entrada'
import { Ronda } from './telas/Ronda'
import { Estoque } from './telas/Estoque'
import { Trato } from './telas/Trato'
import { Tanques } from './telas/Tanques'
import {
  ErroDoServidor, esquecerLembrete, lembrete, listarAlertas, precosNaFila, quemSouEu, sair,
  type Alerta, type Eu, type Perfil,
} from './acesso/api'
import { diasEntreDatas } from './dominio/unidades'

type Aba =
  | 'ronda' | 'trato'
  | 'biometria' | 'ciclos' | 'tanques' | 'acessos'
  | 'despesca' | 'romaneios' | 'movimentacao' | 'recepcao' | 'aplicacao' | 'perdas'
  | 'estoque' | 'relatorios' | 'graficos'

interface Destino {
  chave: Aba
  rotulo: string
  perfis: readonly Perfil[]
  /** Ainda não construída: aparece no menu, apagada, e não abre. */
  emBreve?: boolean
}

/**
 * DUAS ABAS EM CIMA, O RESTO NO MENU — decisão do gestor em 20/09/2026,
 * depois de ver a tela com ele e com o peixeiro.
 *
 * Eram seis abas competindo pelo mesmo olhar, e no celular isso não é
 * escolha: é ruído. O que se usa todo dia, de pé na beira do tanque, são
 * duas coisas — ler a água e registrar o trato. Tudo o mais é do escritório
 * ou de evento, e evento tem tempo de procurar.
 *
 * A barra de cima é a ROTINA. O menu é o RESTO.
 */
const ROTINA: readonly Destino[] = [
  { chave: 'ronda', rotulo: 'Ronda', perfis: ['tratador', 'gestor', 'administrador'] },
  { chave: 'trato', rotulo: 'Trato', perfis: ['tratador', 'gestor', 'administrador'] },
]

/**
 * O menu, em três blocos separados por linha: o que já existe, o que vem, e
 * a administração. As que estão `emBreve` aparecem de propósito — um mapa com
 * os buracos marcados vale mais que um mapa que finge estar completo, e é
 * assim que o gestor enxerga o que falta sem ter de perguntar.
 */
const MENU: readonly Destino[] = [
  { chave: 'ciclos', rotulo: 'Ciclos', perfis: ['tratador', 'gestor', 'administrador'] },
  // É DO TRATADOR também: quem está com o peixe na mão e a balança do lado é
  // ele, e a pesagem precisa poder ser registrada ali, sem sinal, com a fila
  // segurando até a volta.
  //
  // OS SEIS MANEJOS, na ordem em que acontecem num ciclo: recebe, pesa, move,
  // aplica, perde, despesca. Aqui ficam na ordem de uso, com a Pesagem na
  // frente por ser a mais frequente. Ver docs/MANEJOS.md.
  //
  // `biometria` continua sendo a chave desta entrada, e não `pesagem`: mudá-la
  // deixaria fora de lugar quem tivesse o app aberto na aba antiga, e chave é
  // endereço, não rótulo.
  { chave: 'biometria', rotulo: 'Pesagem', perfis: ['tratador', 'gestor', 'administrador'] },
  { chave: 'tanques', rotulo: 'Tanques', perfis: ['gestor', 'administrador'] },
  // A DESPESCA é do tratador também: quem está com a rede na mão e o cestão
  // na balança é ele, e uma pessoa só registra — decisão do gestor.
  { chave: 'despesca', rotulo: 'Despesca', perfis: ['tratador', 'gestor', 'administrador'] },
  // OS ROMANEIOS, logo abaixo da Despesca: é onde se procura o papel de uma
  // venda já feita, para reenviar ao comprador (06/10/2026). Mesmos perfis da
  // Despesca — quem registra a venda e digita o preço é quem pode reabri-la.
  { chave: 'romaneios', rotulo: 'Romaneios', perfis: ['tratador', 'gestor', 'administrador'] },
  // A MOVIMENTAÇÃO é transferência e repicagem na mesma ficha. Também do
  // tratador: quem puxa a rede e carrega o trator é ele.
  { chave: 'movimentacao', rotulo: 'Movimentação', perfis: ['tratador', 'gestor', 'administrador'] },
  { chave: 'recepcao', rotulo: 'Recepção', perfis: ['tratador', 'gestor', 'administrador'] },
  { chave: 'aplicacao', rotulo: 'Aplicação', perfis: ['tratador', 'gestor', 'administrador'] },
  { chave: 'perdas', rotulo: 'Perdas', perfis: ['tratador', 'gestor', 'administrador'] },
  // O ESTOQUE É DO TRATADOR TAMBÉM, e tem de ser: quem tira o saco da pilha é
  // ele, e a baixa só é confiável no momento em que a ração sai do galpão.
  // Exigir o gestor faria a baixa virar tarefa de escritório, lançada de
  // memória no fim do dia — que é o mesmo que não ter. Só o valor da nota
  // fica de fora, e é o servidor que recusa.
  { chave: 'estoque', rotulo: 'Estoque de ração', perfis: ['tratador', 'gestor', 'administrador'] },
  { chave: 'relatorios', rotulo: 'Relatórios', perfis: ['gestor', 'administrador'], emBreve: true },
  { chave: 'graficos', rotulo: 'Gráficos', perfis: ['gestor', 'administrador'], emBreve: true },
  { chave: 'acessos', rotulo: 'Acessos', perfis: ['administrador'] },
]

export function App() {
  const [eu, setEu] = useState<Eu | null>(lembrete)
  const [conferindo, setConferindo] = useState(true)
  const [aba, setAba] = useState<Aba | null>(null)
  const [menuAberto, setMenuAberto] = useState(false)
  const [saindo, setSaindo] = useState(false)
  // Só para a confirmação de sair poder dizer quanto ficaria parado.
  const pendentesAgora = quantosPendentes()
  // Sobe a cada sincronização: é o que faz as telas voltarem a ler do banco.
  const [chave, setChave] = useState(0)
  const [alertas, setAlertas] = useState<Alerta[]>([])

  // OS AVISOS DO ESCRITÓRIO — pedido do gestor em 25/09/2026. O ADM pode ficar
  // semanas sem abrir o estoque; o aviso vai até ele, na abertura do app, e se
  // relê a cada sincronização. O tratador não recebe: preço não é da raia dele.
  // Sem sinal, nenhum aviso — e o app abre igual.
  const perfilAtual = eu?.perfil ?? null
  useEffect(() => {
    if (perfilAtual === null || perfilAtual === 'tratador') {
      setAlertas([])
      return
    }
    listarAlertas().then(setAlertas).catch(() => setAlertas([]))
  }, [perfilAtual, chave])

  useEffect(() => {
    quemSouEu()
      .then(setEu)
      .catch((falha: unknown) => {
        // SÓ UMA RECUSA EXPLÍCITA DO CRACHÁ DEVOLVE À TELA DO CÓDIGO — é a
        // regra do CLAUDE.md, e o código não a cumpria: qualquer erro que não
        // fosse «sem sinal» deslogava, inclusive um 502 passageiro da
        // Cloudflare com sinal fraco. O tratador na beira do tanque ficava sem
        // entrar até ter sinal E um código novo do gestor. Achado na revisão
        // de 25/09/2026.
        if (falha instanceof ErroDoServidor && (falha.status === 401 || falha.status === 403)) {
          esquecerLembrete()
          setEu(null)
        }
      })
      .finally(() => setConferindo(false))
  }, [])

  if (!eu) {
    if (conferindo) return <p className="aviso">Abrindo…</p>
    return <Entrada aoEntrar={setEu} />
  }

  const rotina = ROTINA.filter((a) => a.perfis.includes(eu.perfil))
  const menu = MENU.filter((a) => a.perfis.includes(eu.perfil))
  const todas = [...rotina, ...menu]
  // A aba padrão é a PRIMEIRA desta pessoa, não uma fixa. Com uma fixa, o
  // tratador abriria numa tela que não é dele e veria «esta tela é do gestor».
  const visivel: Aba = todas.some((a) => a.chave === aba && !a.emBreve)
    ? (aba as Aba)
    : (rotina[0]?.chave ?? 'ronda')
  const naRotina = rotina.some((a) => a.chave === visivel)
  const atual = todas.find((a) => a.chave === visivel)

  return (
    <>
      {/* O ☰ FICA NA ESQUERDA, colado no nome da fazenda, e o Sair na ponta
          oposta. Estavam lado a lado, e o gestor pegou: quem abre o menu
          dezenas de vezes por dia acaba, um dia, acertando o vizinho — e o
          vizinho era o botão que tira a pessoa do app. Separar é mais barato
          que qualquer aviso. */}
      <header className="cabecalho">
        <div className="cabecalho-inicio">
          {menu.length > 0 && (
            <button
              type="button"
              className="menu-abrir"
              onClick={() => setMenuAberto(true)}
              aria-label="abrir o menu"
            >
              ☰
            </button>
          )}
          <div>
            <strong>{eu.fazenda}</strong>
            <div className="secundario">{eu.nome} · {eu.perfil}</div>
          </div>
        </div>
        <button type="button" className="discreto" onClick={() => setSaindo(true)}>
          Sair
        </button>
      </header>

      {/* SAIR PEDE CONFIRMAÇÃO, e a confirmação diz o PREÇO em vez de
          perguntar «tem certeza?».
          Sair apaga o crachá deste aparelho, e crachá não se recupera: para
          voltar é preciso um código novo, que só o gestor gera e que chega
          por WhatsApp. Para o tratador, num domingo, na beira do tanque, isso
          é o app morrer até segunda.
          E se houver fila, ela fica guardada — mas parada, porque ninguém
          sincroniza deslogado. Dizer isso é o que faz alguém desistir a tempo. */}
      {saindo && (
        <div className="confirmar-sair">
          <div>
            <strong>Sair mesmo?</strong>
            <div className="secundario">
              Para entrar de novo você vai precisar de um código novo, gerado pelo gestor.
              {pendentesAgora > 0 && (
                <>
                  {' '}
                  E há <strong>{pendentesAgora}</strong>{' '}
                  {pendentesAgora === 1 ? 'registro esperando' : 'registros esperando'} para
                  subir: ficam guardados no aparelho, mas parados até alguém entrar de novo.
                </>
              )}
            </div>
          </div>
          <div className="confirmar-acoes">
            <button
              type="button"
              className="botao-perigo"
              onClick={() => {
                void sair().finally(() => setEu(null))
              }}
            >
              Sair
            </button>
            <button type="button" className="discreto" onClick={() => setSaindo(false)}>
              não, voltar
            </button>
          </div>
        </div>
      )}

      {/* O AVISO DO ESCRITÓRIO, entre o cabeçalho e as abas: é a primeira
          coisa que se lê, e tocar leva direto à lista do que falta. Na própria
          tela de Estoque ele some — lá a lista inteira já está à vista. */}
      {visivel !== 'estoque' && alertas.map((a) => {
        // DESCONTA O QUE JÁ ESTÁ NA FILA. Preço lançado e ainda não
        // sincronizado não é trabalho pendente — é trabalho feito esperando
        // sinal, e o contador do Sincronizar já diz isso.
        const naFila = precosNaFila().compras
        const faltam = a.compras.filter((c) => !naFila.has(c.id))
        if (faltam.length === 0) return null
        const maisAntiga = faltam.reduce((m, c) => (c.data < m ? c.data : m), faltam[0]?.data ?? '')
        return (
          <button
            key={a.tipo}
            type="button"
            className="alerta-escritorio"
            onClick={() => setAba('estoque')}
          >
            <strong>
              {faltam.length} {faltam.length === 1 ? 'compra' : 'compras'} de ração sem preço
            </strong>
            {' — '}
            {quandoFoi(maisAntiga)} · lançar o preço da nota ›
          </button>
        )
      })}

      {rotina.length > 1 && (
        <nav className="abas">
          {rotina.map(({ chave, rotulo }) => (
            <button
              key={chave}
              type="button"
              className={visivel === chave ? 'aba ativa' : 'aba'}
              onClick={() => setAba(chave)}
            >
              {rotulo}
            </button>
          ))}
        </nav>
      )}

      {/* Fora da rotina, a tela diz onde você está e como voltar. Sem isto,
          quem entrou pelo menu fica numa tela sem nome e sem saída visível. */}
      {!naRotina && atual && (
        <div className="fora-da-rotina">
          <strong>{atual.rotulo}</strong>
          <button type="button" className="discreto" onClick={() => setAba('ronda')}>
            voltar para a ronda
          </button>
        </div>
      )}

      {menuAberto && (
        <MenuLateral
          itens={menu}
          atual={visivel}
          aoEscolher={(c) => {
            setAba(c)
            setMenuAberto(false)
          }}
          aoFechar={() => setMenuAberto(false)}
        />
      )}

      <BarraDeSincronia aoAtualizar={() => setChave((c) => c + 1)} />

      {/* A aba escolhida só vale se ela for de fato desta pessoa. Sem esta
          conferência, um perfil rebaixado enquanto o app está aberto
          continuaria vendo a tela antiga até alguém recarregar a página.

          `key` força a tela a recarregar do servidor depois de sincronizar:
          o que subiu deixou de ser fila e virou dado, e a tela tem de passar
          a ler do banco em vez de somar o que guardava. */}
      {visivel === 'ronda' ? <Ronda key={chave} />
        : visivel === 'trato' ? <Trato key={chave} />
        : visivel === 'acessos' ? <Acessos />
        : visivel === 'ciclos' ? <Ciclos key={chave} perfil={eu.perfil} />
        : visivel === 'tanques' ? <Tanques />
        : visivel === 'despesca'
          ? <Despesca key={chave} perfil={eu.perfil} aoGravar={() => setChave((c) => c + 1)} />
        : visivel === 'romaneios' ? <Romaneios key={chave} perfil={eu.perfil} />
        : visivel === 'movimentacao'
          ? <Movimentacao key={chave} aoGravar={() => setChave((c) => c + 1)} />
        : visivel === 'recepcao'
          ? <Recepcao key={chave} aoGravar={() => setChave((c) => c + 1)} />
        : visivel === 'aplicacao'
          ? <Aplicacao key={chave} aoGravar={() => setChave((c) => c + 1)} />
        : visivel === 'perdas'
          ? <Perdas key={chave} aoGravar={() => setChave((c) => c + 1)} />
        : visivel === 'estoque'
          ? <Estoque key={chave} perfil={eu.perfil} aoGravar={() => setChave((c) => c + 1)} />
        : <Biometria key={chave} perfil={eu.perfil} />}
    </>
  )
}

/**
 * O MENU LATERAL — o que não é rotina.
 *
 * Entra por cima da tela, e não empurrando o conteúdo: empurrar faria a
 * página inteira pular sob o dedo de quem só queria olhar a lista.
 *
 * Fecha no X, no fundo escuro e no Esc. Três saídas para um painel que cobre
 * a tela é o mínimo — quem abriu sem querer precisa sair sem pensar.
 */
function MenuLateral({
  itens, atual, aoEscolher, aoFechar,
}: {
  itens: readonly Destino[]
  atual: Aba
  aoEscolher: (c: Aba) => void
  aoFechar: () => void
}) {
  useEffect(() => {
    const noEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar()
    }
    window.addEventListener('keydown', noEsc)
    return () => window.removeEventListener('keydown', noEsc)
  }, [aoFechar])

  const prontos = itens.filter((i) => !i.emBreve)
  const vindo = itens.filter((i) => i.emBreve)

  return (
    <div className="menu-fundo" onClick={aoFechar} role="presentation">
      <nav
        className="menu-lateral"
        onClick={(e) => e.stopPropagation()}
        aria-label="outras telas"
      >
        <div className="menu-topo">
          <strong>Outras telas</strong>
          <button type="button" className="discreto" onClick={aoFechar} aria-label="fechar">
            ✕
          </button>
        </div>

        {prontos.map((i) => (
          <button
            key={i.chave}
            type="button"
            className={atual === i.chave ? 'menu-item ativo' : 'menu-item'}
            onClick={() => aoEscolher(i.chave)}
          >
            {i.rotulo}
          </button>
        ))}

        {vindo.length > 0 && (
          <>
            <div className="menu-separador">ainda não construídas</div>
            {vindo.map((i) => (
              <button key={i.chave} type="button" className="menu-item em-breve" disabled>
                {i.rotulo}
                <span className="secundario">em breve</span>
              </button>
            ))}
          </>
        )}
      </nav>
    </div>
  )
}

/**
 * O BOTÃO SINCRONIZAR — e por que ele faz três coisas, e não uma.
 *
 * O gestor perguntou se «enviar os dados», «receber o banco atualizado» e
 * «atualizar a versão do app» eram três pedidos diferentes. São três
 * mecanismos diferentes, sim — mas um gesto só, e nesta ordem, porque a ordem
 * é o que os torna seguros:
 *
 *   1. SOBE a fila. Enquanto houver registro no aparelho que o servidor não
 *      conhece, nada mais deve acontecer.
 *   2. RECARREGA a tela, que volta a ler do banco. Isto não é opcional: o que
 *      acabou de subir precisa parar de ser contado duas vezes, uma na fila e
 *      outra no servidor.
 *   3. SÓ ENTÃO oferece a versão nova do app, e só se a fila esvaziou.
 *      Recarregar o app com registro pendente é o único jeito de perder dado
 *      aqui — código novo pode ler a fila de um jeito que o código velho
 *      escreveu. Fila vazia primeiro, versão nova depois.
 *
 * O contador mostra só o número, como o gestor pediu. Registro recusado pelo
 * servidor não some do contador — e é justamente por não sumir que alguém
 * acaba perguntando o que é aquilo.
 */
function BarraDeSincronia({ aoAtualizar }: { aoAtualizar: () => void }) {
  const [pendentes, setPendentes] = useState(quantosPendentes())
  const [subindo, setSubindo] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)
  const [versaoNova, setVersaoNova] = useState(false)
  const [abrindoFila, setAbrindoFila] = useState(false)

  useEffect(() => assinar(() => setPendentes(quantosPendentes())), [])

  const sincronizarTudo = useCallback(async () => {
    setSubindo(true)
    setRecado(null)
    try {
      const r = await sincronizar()
      aoAtualizar()

      // OS RECADOS SÃO CURTOS, e cada um diz uma coisa só — pedido do gestor
      // em 25/09/2026, depois de ler «sem sinal» como «o banco recusou».
      if (r.semSinal || r.falhou || r.semAcesso) {
        const motivo = r.semAcesso
          ? 'O acesso deste aparelho caiu'
          : r.falhou
            ? 'Conexão falhou'
            : 'Não há conexão'
        const quanto =
          r.enviados > 0
            ? `${r.enviados} enviados, ${r.restantes} continuam na fila.`
            : 'nada foi enviado.'
        // O caminho de volta, dito: sem ele o tratador aperta Sincronizar o dia
        // inteiro e lê a mesma frase.
        const saida = r.semAcesso
          ? ' Feche e abra o app; se ele pedir código, peça um novo ao gestor. A fila fica guardada.'
          : ''
        // Uma recusa do mesmo envio não pode sumir atrás do recado de conexão.
        const recusa =
          r.recusados > 0
            ? ` E ${r.recusados} registro(s) o servidor recusou — mostre ao gestor.`
            : ''
        // O MESMO REGISTRO VOLTANDO COM ERRO não é sinal: é ele. Dizer qual é
        // o que evita a única outra saída — apagar a fila inteira. E o recado
        // manda MOSTRAR, não apagar: o registro pode estar certo (um trato
        // apagado seria dado de novo), e quem decide é quem vê o servidor.
        const qual = r.travado
          ? [NOME[r.travado.tipo], r.travado.rotulo, r.travado.resumo]
              .filter((x) => x !== null && x !== '')
              .join(' · ')
          : ''
        const trava = r.travado
          ? ` O registro «${qual}» já voltou com erro do servidor ${r.travado.vezes} vezes` +
            ' seguidas, e é ele que está segurando a fila — mostre ao gestor antes de apagar.'
          : ''
        setRecado(`${motivo} — ${quanto}${saida}${recusa}${trava}`)
        return
      }
      if (r.recusados > 0) {
        setRecado(`${r.recusados} registro(s) o servidor recusou. Mostre esta tela ao gestor.`)
        return
      }
      setRecado(r.enviados > 0 ? `${r.enviados} enviados. Tudo em dia.` : 'tudo em dia.')

      // Passo 3: só com a fila vazia.
      if (r.restantes === 0) await procurarVersaoNova(setVersaoNova)
    } finally {
      setSubindo(false)
    }
  }, [aoAtualizar])

  return (
    <>
      <div className={pendentes > 0 ? 'sincronia com-fila' : 'sincronia'}>
        <button
          type="button"
          className="sincronia-botao"
          onClick={() => void sincronizarTudo()}
          disabled={subindo}
        >
          {subindo ? 'Enviando…' : 'Sincronizar'}
          {pendentes > 0 && <span className="sincronia-contador">{pendentes}</span>}
        </button>

        {/* «ver» é o que transforma o contador em coisa que se pode conferir.
            Sem ele o número diz que há algo guardado e não diz o quê. */}
        {/* Some quando não há nada E o painel está fechado — assim quem
            esvaziou a fila ainda tem por onde fechar o que abriu. */}
        {(pendentes > 0 || abrindoFila) && (
          <button
            type="button"
            className="discreto"
            onClick={() => setAbrindoFila((a) => !a)}
          >
            {abrindoFila ? 'fechar' : 'ver o que está esperando'}
          </button>
        )}

        {recado && <span className="sincronia-recado">{recado}</span>}

        {versaoNova && (
          <button type="button" className="sincronia-versao" onClick={() => location.reload()}>
            Há uma versão nova do app — atualizar
          </button>
        )}
      </div>

      {abrindoFila && (
        <section className="bloco">
          <Fila aoMudar={aoAtualizar} />
        </section>
      )}
    </>
  )
}

/**
 * Pergunta à Cloudflare se há código novo publicado.
 *
 * `registration.update()` busca o `sw.js` do servidor e compara. Como o
 * arquivo carrega a VERSAO, qualquer publicação nossa muda o conteúdo e o
 * navegador instala o novo. Quem decide recarregar é o tratador, no botão —
 * recarregar sozinho no meio da ronda jogaria fora a tela que ele estava
 * preenchendo.
 */
async function procurarVersaoNova(avisar: (ha: boolean) => void): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  try {
    const registro = await navigator.serviceWorker.getRegistration()
    if (!registro) return
    await registro.update()
    if (registro.installing || registro.waiting) avisar(true)
  } catch {
    // Sem sinal ou sem service worker. Não é erro do tratador e não vira aviso.
  }
}

/** «a mais antiga há 23 dias» — dito como se diz, não como se calcula. */
function quandoFoi(iso: string): string {
  const hoje = new Date()
  const dias = diasEntreDatas(
    iso,
    `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`,
  )
  if (dias <= 0) return 'a mais antiga é de hoje'
  if (dias === 1) return 'a mais antiga é de ontem'
  return `a mais antiga há ${dias} dias`
}
